import "~/lib/browser-buffer-polyfill";
import { QueryClientProvider } from "@tanstack/react-query";
import { LazyMotion, MotionConfig } from "framer-motion";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { overwriteGetLocale, overwriteSetLocale } from "~/lib/i18n";
import { ResolvedAppProviders } from "~/app-bootstrap";
import { preloadPrivyAppRuntimeProvider } from "~/components/lazy-privy-app-provider";
import { RootErrorBoundary } from "~/components/root-error-boundary";
import { ThemeProvider } from "~/components/theme-provider";
import { isExecutionStreamBrowserE2EMode } from "~/lib/e2e/is-execution-stream-browser-e2e-mode";
import { isMarketDataStreamBrowserE2EMode } from "~/lib/e2e/is-market-data-stream-browser-e2e-mode";
import { isWalletlessBrowserE2EMode } from "~/lib/e2e/is-walletless-browser-e2e-mode";
import { getPrivyAppId } from "~/lib/env";
import { preloadInitialRouteModule } from "~/initial-route-preload";
import { getActiveLocale, setLanguageLocale } from "~/lib/language-lens";
import { queryClient } from "~/lib/query-client";
import { installWebappV2ReleaseRecoveryHandlers } from "~/lib/release-recovery";
import { schedulePostPaint } from "~/lib/schedule-post-paint";
import { createSentryRootOptions, initSentry } from "~/lib/sentry";
import { initializeRouteVisit } from "~/lib/route-visit";
import { router } from "~/router";
import "~/styles/app.css";

overwriteGetLocale(() => getActiveLocale());
overwriteSetLocale((locale) => {
  setLanguageLocale(locale);
});

const loadFramerMotionFeatures = () =>
  import("~/lib/framer-motion-features").then((module) => module.default);

const rootElement = document.getElementById("app");

if (!rootElement) {
  throw new Error("Could not find root element with id `app`.");
}

const root = createRoot(rootElement, createSentryRootOptions());

initializeRouteVisit(router);
initSentry(router);
installWebappV2ReleaseRecoveryHandlers();

// Start known public browse graphs alongside provider/auth bootstrap. Routes
// remain code-split; this only removes the serial wait before their first
// above-the-fold render.
void preloadInitialRouteModule(window.location.pathname);

if (
  getPrivyAppId() !== null &&
  !router.state.location.pathname.startsWith("/design-system") &&
  !isExecutionStreamBrowserE2EMode() &&
  !isMarketDataStreamBrowserE2EMode() &&
  !isWalletlessBrowserE2EMode()
) {
  void preloadPrivyAppRuntimeProvider();
}

root.render(
  <StrictMode>
    <RootErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          {/*
            Gate all framer-motion animations on the user's prefers-reduced-motion
            setting. When reduced motion is requested, transform/layout animations
            (the m.article layout="position" on chat messages, the spring
            entrance, the ping/pulse indicators) are disabled while opacity
            transitions remain. This is the single highest-leverage a11y fix for
            the agent chat surface, which animates on every 50ms-throttled stream
            chunk across a full message list.
          */}
          <LazyMotion features={loadFramerMotionFeatures} strict>
            <MotionConfig reducedMotion="user">
              <ResolvedAppProviders />
            </MotionConfig>
          </LazyMotion>
        </QueryClientProvider>
      </ThemeProvider>
    </RootErrorBoundary>
  </StrictMode>,
);

schedulePostPaint(() => {
  void import("~/lib/release-version").then(
    ({ installReleaseVersionWatcher }) => installReleaseVersionWatcher(),
  );
}, 250);
